import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Grid,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  PageHeader,
  SearchFilterBar,
  DataTable,
  StatusChip,
  FormDialog,
  flattenPages,
} from '../components';
import { useProductsPaginated, useCreateProduct } from '../hooks/useProducts';
import { useCategories } from '../hooks/useCategories';
import { useBusinessUuid } from '../useBusinessUuid';
import { colors, formatCurrency } from '../constants';

const UNIT_OPTIONS = [
  'piece', 'each', 'box', 'case', 'pack', 'dozen', 'kg', 'g', 'lb', 'oz',
  'liter', 'ml', 'gallon', 'bottle', 'can', 'bag', 'roll', 'sheet', 'pair', 'set',
];

const emptyForm = {
  name: '',
  sku: '',
  description: '',
  categoryId: '',
  unit: 'piece',
  unitPrice: '0',
  stock: '0',
  minStockLevel: '0',
  maxStockLevel: '0',
  trackInventory: true,
};

export default function Products() {
  const history = useHistory();
  const businessUuid = useBusinessUuid();
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [stockFilter, setStockFilter] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState(null);

  const params = useMemo(
    () => ({
      limit: 20,
      active: activeFilter ? activeFilter === 'true' : undefined,
      search: search || undefined,
      sortField: 'productDetails.name',
      sortOrder: 'asc',
      stockStatus: stockFilter || undefined,
    }),
    [search, activeFilter, stockFilter]
  );

  const query = useProductsPaginated(params);
  const createProduct = useCreateProduct();
  const { data: categories = [], isLoading: categoriesLoading } =
    useCategories(businessUuid);

  const products = flattenPages(query);
  const totalCount = query.data?.pages?.[0]?.total ?? 0;

  const setField = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const openCreate = () => {
    setForm(emptyForm);
    setError(null);
    setOpen(true);
  };

  const handleSubmit = async () => {
    setError(null);
    if (!form.name.trim() || !form.sku.trim()) {
      setError('Name and SKU are required.');
      return;
    }
    if (!form.categoryId) {
      setError('Menu category is required.');
      return;
    }
    const selectedCategory = categories.find((c) => c.id === form.categoryId);
    try {
      await createProduct.mutateAsync({
        businessUuid,
        name: form.name,
        sku: form.sku,
        description: form.description,
        unit: form.unit,
        unitPrice: parseFloat(form.unitPrice) || 0,
        stock: parseInt(form.stock, 10) || 0,
        minStockLevel: parseInt(form.minStockLevel, 10) || 0,
        maxStockLevel: parseInt(form.maxStockLevel, 10) || 0,
        trackInventory: form.trackInventory,
        categoryId: form.categoryId || null,
        categoryName: selectedCategory?.name ?? null,
        origin: 'inventory',
      });
      setOpen(false);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Something went wrong');
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Product',
      render: (row) => (
        <>
          <Typography variant="body2" sx={{ fontWeight: 600, color: colors.navy }}>
            {row.name}
          </Typography>
          <Typography variant="caption" sx={{ color: colors.gray500 }}>
            SKU: {row.sku}
          </Typography>
        </>
      ),
    },
    { key: 'unit', label: 'Unit' },
    {
      key: 'unitPrice',
      label: 'Cost',
      align: 'right',
      render: (row) => formatCurrency(row.unitPrice),
    },
    {
      key: 'stock',
      label: 'Stock',
      align: 'right',
      render: (row) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: row.stock <= row.minStockLevel ? colors.error : colors.navy,
          }}
        >
          {row.stock}
        </Typography>
      ),
    },
    {
      key: 'isActive',
      label: 'Status',
      render: (row) => <StatusChip status={row.isActive ? 'active' : 'inactive'} />,
    },
  ];

  return (
    <>
      <PageHeader
        title={`Products${totalCount ? ` (${totalCount})` : ''}`}
        actionLabel="New Product"
        actionIcon={<AddIcon />}
        onAction={openCreate}
      />

      <SearchFilterBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by name, SKU, or barcode..."
        filters={[
          {
            label: 'Status',
            value: activeFilter,
            onChange: setActiveFilter,
            options: [
              { value: 'true', label: 'Active' },
              { value: 'false', label: 'Inactive' },
            ],
          },
          {
            label: 'Stock',
            value: stockFilter,
            onChange: setStockFilter,
            options: [
              { value: 'low_stock', label: 'Low Stock' },
              { value: 'out_of_stock', label: 'Out of Stock' },
              { value: 'no_cost', label: 'No Cost' },
            ],
          },
        ]}
      />

      <DataTable
        columns={columns}
        rows={products}
        loading={query.isLoading}
        onRowClick={(row) => history.push(`/inventory-management/products/${row.uuid}`)}
        emptyTitle="No products found"
        emptyMessage="Create your first product to get started."
        hasNextPage={query.hasNextPage}
        isFetchingNextPage={query.isFetchingNextPage}
        fetchNextPage={query.fetchNextPage}
      />

      <FormDialog
        open={open}
        title="New Product"
        onClose={() => setOpen(false)}
        onSubmit={handleSubmit}
        submitLabel="Create Product"
        loading={createProduct.isPending}
      >
        {error && (
          <Typography variant="body2" sx={{ color: colors.error, mb: 1.5 }}>
            {error}
          </Typography>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField label="Name" value={form.name} onChange={setField('name')} fullWidth required size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="SKU" value={form.sku} onChange={setField('sku')} fullWidth required size="small" />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Description" value={form.description} onChange={setField('description')} fullWidth multiline minRows={2} size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Menu Category"
              value={form.categoryId}
              onChange={setField('categoryId')}
              fullWidth
              required
              size="small"
              helperText={categoriesLoading ? 'Loading categories...' : categories.length === 0 ? 'No menu categories available' : ' '}
            >
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select label="Unit" value={form.unit} onChange={setField('unit')} fullWidth size="small">
              {UNIT_OPTIONS.map((u) => (
                <MenuItem key={u} value={u}>{u}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField label="Unit Cost" type="number" value={form.unitPrice} onChange={setField('unitPrice')} fullWidth size="small" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField label="Initial Stock" type="number" value={form.stock} onChange={setField('stock')} fullWidth size="small" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField label="Min Stock" type="number" value={form.minStockLevel} onChange={setField('minStockLevel')} fullWidth size="small" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField label="Max Stock" type="number" value={form.maxStockLevel} onChange={setField('maxStockLevel')} fullWidth size="small" />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.trackInventory}
                  onChange={(e) => setForm((f) => ({ ...f, trackInventory: e.target.checked }))}
                />
              }
              label="Track Inventory"
            />
          </Grid>
        </Grid>
      </FormDialog>
    </>
  );
}
